import { createAccessControl } from "better-auth/plugins/access";
import { adminAc, defaultStatements } from "better-auth/plugins/admin/access";

const hiiveStatements = [
  "create",
  "view",
  "update",
  "list",
] as readonly string[];
const venueStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
] as readonly string[];
const bookingStatements = [
  "create",
  "view",
  "update",
  "cancel",
  "refund",
  "list",
] as readonly string[];

const tierStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
] as readonly string[];
const queenStatements = [
  "view",
  "create",
  "update",
  "delete",
  "list",
] as readonly string[];
const membershipStatements = [
  "join",
  "leave",
  "invite",
  "approve",
  "reject",
  "ban",
  "unban",
  "view",
  "list",
] as readonly string[];
const postStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "react",
  "comment",
  "pin",
] as readonly string[];
const eventStatements = [
  "create",
  "view",
  "update",
  "delete",
  "manage_tickets",
  "manage_schedule",
  "check_in",
] as readonly string[];
const chatStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "join",
  "leave",
] as readonly string[];
const messageStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "react",
] as readonly string[];
const promotionStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "analytics",
] as readonly string[];
const campaignStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "send",
  "approve",
  "analytics",
] as readonly string[];
const financeStatements = [
  "view",
  "manage_accounts",
  "view_documents",
  "approve",
  "export",
  "mark_paid",
  "adjust",
] as readonly string[];
const shopStatements = [
  "create",
  "view",
  "update",
  "delete",
  "list",
  "fulfill",
] as readonly string[];
// Hiive Money (platform credits) admin actions. Platform-level only — never a
// hiive-scoped (queen) resource.
const creditStatements = [
  "view", // see balances, ledger, and the liability dashboard
  "issue", // grant credits (single or bulk / segment)
  "revoke", // claw back a grant
  "adjust", // manual balance correction
] as readonly string[];

// Referral program admin actions. Platform-level only. `configure` is the
// dangerous one — it changes how much real money every future referral costs —
// so it is deliberately separable from plain `view`.
const referralStatements = [
  "view", // funnel, cost, advocate leaderboard, fraud clusters
  "configure", // edit amounts, triggers, caps, windows, issue mode
  "approve", // release a reward from the review queue
  "void", // cancel an unclaimed reward or a whole referral edge
  "pause", // the kill switch
] as readonly string[];

// Platform admin-panel sections. Each value is a navigable area of /admin and
// doubles as the access grant for restricted (admin_staff) users.
export const ADMIN_SECTIONS = [
  "overview",
  "hiives",
  "venues",
  "events",
  "games",
  "movement",
  "users",
  "finance",
  "promotions",
  "offers",
  "shop",
  "credits",
  "referrals",
  "notifications",
  "reports",
  "reviews",
  "advanced",
  "team",
] as const;
export type AdminSection = (typeof ADMIN_SECTIONS)[number];
const adminSectionStatements = ADMIN_SECTIONS as readonly string[];

const statement = {
  ...defaultStatements,
  hiive: hiiveStatements,
  venue: venueStatements,
  booking: bookingStatements,
  event: eventStatements,
  tier: tierStatements,
  queen: queenStatements,
  membership: membershipStatements,
  post: postStatements,
  chat: chatStatements,
  message: messageStatements,
  promotion: promotionStatements,
  campaign: campaignStatements,
  finance: financeStatements,
  shop: shopStatements,
  credit: creditStatements,
  referral: referralStatements,
  adminSection: adminSectionStatements,
} as const;

export const ac = createAccessControl(statement);

export type HiivePermission = Partial<{
  hiive: (typeof hiiveStatements)[number][];
  venue: (typeof venueStatements)[number][];
  booking: (typeof bookingStatements)[number][];
  event: (typeof eventStatements)[number][];
  tier: (typeof tierStatements)[number][];
  queen: (typeof queenStatements)[number][];
  membership: (typeof membershipStatements)[number][];
  post: (typeof postStatements)[number][];
  chat: (typeof chatStatements)[number][];
  message: (typeof messageStatements)[number][];
  promotion: (typeof promotionStatements)[number][];
  campaign: (typeof campaignStatements)[number][];
  finance: (typeof financeStatements)[number][];
  shop: (typeof shopStatements)[number][];
}>;

export const QUEEN_MODERATOR_PERMISSION_RESOURCE_META: Record<
  keyof HiivePermission,
  { label: string; description: string }
> = {
  hiive: {
    label: "Workspace",
    description: "Base hiive workspace visibility.",
  },
  event: {
    label: "Events",
    description: "Event setup, operations, tickets, schedules, and check-ins.",
  },
  venue: {
    label: "Venues",
    description: "Venue setup and venue operations.",
  },
  booking: {
    label: "Bookings",
    description: "Booking visibility, changes, and cancellations.",
  },
  membership: {
    label: "Members",
    description: "Join requests, member invites, and member restrictions.",
  },
  post: {
    label: "Posts",
    description: "Hiive feed publishing and moderation.",
  },
  chat: {
    label: "Chats",
    description: "Chat room access and moderation.",
  },
  message: {
    label: "Messages",
    description: "Message-level posting and moderation.",
  },
  tier: {
    label: "Tiers",
    description: "Tier visibility for membership operations.",
  },
  queen: {
    label: "Team",
    description: "Queen team roster and role management.",
  },
  promotion: {
    label: "Promotions",
    description: "Promotion setup, changes, and reporting.",
  },
  campaign: {
    label: "Campaigns",
    description: "Campaign drafts, audience previews, tests, and reporting.",
  },
  finance: {
    label: "Finance",
    description: "Payout visibility, settlement documents, and account status.",
  },
  shop: {
    label: "Shop",
    description: "Shop items, orders, and fulfillment for this hiive.",
  },
};

export const QUEEN_MODERATOR_PERMISSION_OPTIONS = [
  {
    resource: "event",
    action: "view",
    label: "View events",
    description: "Open event operation screens.",
  },
  {
    resource: "event",
    action: "create",
    label: "Create events",
    description: "Start new event setup for this hiive.",
  },
  {
    resource: "event",
    action: "update",
    label: "Edit event details",
    description: "Update event settings, content, and configuration.",
  },
  {
    resource: "event",
    action: "delete",
    label: "Delete events",
    description: "Remove events from the hiive workspace.",
  },
  {
    resource: "event",
    action: "manage_tickets",
    label: "Manage tickets",
    description: "View sales, attendees, waitlists, and ticket operations.",
  },
  {
    resource: "event",
    action: "manage_schedule",
    label: "Manage schedule",
    description: "Create, update, and cancel event occurrences.",
  },
  {
    resource: "event",
    action: "check_in",
    label: "Check in attendees",
    description: "Use scanner and check-in workflows.",
  },
  {
    resource: "venue",
    action: "list",
    label: "Browse venues",
    description: "See the venue list for this hiive.",
  },
  {
    resource: "venue",
    action: "view",
    label: "View venues",
    description: "Open venue operations screens.",
  },
  {
    resource: "venue",
    action: "create",
    label: "Create venues",
    description: "Add new venues under this hiive.",
  },
  {
    resource: "venue",
    action: "update",
    label: "Manage venues",
    description: "Edit venue details, availability, and experiences.",
  },
  {
    resource: "venue",
    action: "delete",
    label: "Delete venues",
    description: "Archive or remove venues where supported.",
  },
  {
    resource: "booking",
    action: "view",
    label: "View booking details",
    description: "Open individual event and venue booking records.",
  },
  {
    resource: "booking",
    action: "list",
    label: "View bookings",
    description: "See event and venue bookings.",
  },
  {
    resource: "booking",
    action: "create",
    label: "Create bookings",
    description: "Create operational bookings where supported.",
  },
  {
    resource: "booking",
    action: "update",
    label: "Update bookings",
    description: "Change booking status or details where supported.",
  },
  {
    resource: "booking",
    action: "cancel",
    label: "Cancel bookings",
    description: "Cancel bookings where operationally allowed.",
  },
  {
    resource: "membership",
    action: "view",
    label: "View member details",
    description: "Open member and join request details.",
  },
  {
    resource: "membership",
    action: "list",
    label: "View join requests",
    description: "See membership requests and member lists.",
  },
  {
    resource: "membership",
    action: "invite",
    label: "Invite members",
    description: "Invite people to join this hiive.",
  },
  {
    resource: "membership",
    action: "approve",
    label: "Approve members",
    description: "Approve pending hiive membership requests.",
  },
  {
    resource: "membership",
    action: "reject",
    label: "Reject members",
    description: "Reject pending hiive membership requests.",
  },
  {
    resource: "membership",
    action: "ban",
    label: "Ban members",
    description: "Restrict members from this hiive.",
  },
  {
    resource: "membership",
    action: "unban",
    label: "Unban members",
    description: "Lift a previous member restriction.",
  },
  {
    resource: "post",
    action: "view",
    label: "View posts",
    description: "Open hiive posts.",
  },
  {
    resource: "post",
    action: "list",
    label: "Browse posts",
    description: "See posts in the hiive feed.",
  },
  {
    resource: "post",
    action: "create",
    label: "Create posts",
    description: "Publish posts as a queen for this hiive.",
  },
  {
    resource: "post",
    action: "update",
    label: "Edit posts",
    description: "Update hiive posts.",
  },
  {
    resource: "post",
    action: "delete",
    label: "Delete posts",
    description: "Remove hiive posts.",
  },
  {
    resource: "post",
    action: "pin",
    label: "Pin posts",
    description: "Pin important posts in the hiive feed.",
  },
  {
    resource: "post",
    action: "react",
    label: "React to posts",
    description: "React to posts as this moderator.",
  },
  {
    resource: "post",
    action: "comment",
    label: "Comment on posts",
    description: "Comment on hiive posts.",
  },
  {
    resource: "chat",
    action: "view",
    label: "View chats",
    description: "Open hiive and event chat rooms.",
  },
  {
    resource: "chat",
    action: "list",
    label: "Browse chats",
    description: "See available hiive and event chats.",
  },
  {
    resource: "chat",
    action: "create",
    label: "Create chats",
    description: "Create chats where supported.",
  },
  {
    resource: "chat",
    action: "update",
    label: "Moderate chats",
    description: "Manage hiive and event chat settings.",
  },
  {
    resource: "chat",
    action: "delete",
    label: "Delete chats",
    description: "Remove chats where supported.",
  },
  {
    resource: "chat",
    action: "join",
    label: "Join chats",
    description: "Join hiive and event chats.",
  },
  {
    resource: "chat",
    action: "leave",
    label: "Leave chats",
    description: "Leave hiive and event chats.",
  },
  {
    resource: "message",
    action: "view",
    label: "View messages",
    description: "Read messages in permitted chats.",
  },
  {
    resource: "message",
    action: "list",
    label: "Browse messages",
    description: "Load message history in permitted chats.",
  },
  {
    resource: "message",
    action: "create",
    label: "Send messages",
    description: "Send messages in permitted chats.",
  },
  {
    resource: "message",
    action: "update",
    label: "Edit messages",
    description: "Edit messages where moderation allows.",
  },
  {
    resource: "message",
    action: "delete",
    label: "Delete messages",
    description: "Delete messages where moderation allows.",
  },
  {
    resource: "message",
    action: "react",
    label: "React to messages",
    description: "React to messages in permitted chats.",
  },
  {
    resource: "tier",
    action: "view",
    label: "View tiers",
    description: "Open tier details used for access checks.",
  },
  {
    resource: "tier",
    action: "list",
    label: "Browse tiers",
    description: "See available hiive tiers.",
  },
  {
    resource: "promotion",
    action: "view",
    label: "View promotions",
    description: "Open promotion records.",
  },
  {
    resource: "promotion",
    action: "list",
    label: "Browse promotions",
    description: "See the promotion list for this hiive.",
  },
  {
    resource: "promotion",
    action: "create",
    label: "Create promotions",
    description: "Create promotions funded by the hiive.",
  },
  {
    resource: "promotion",
    action: "update",
    label: "Edit promotions",
    description: "Update promotion setup and status.",
  },
  {
    resource: "promotion",
    action: "delete",
    label: "Delete promotions",
    description: "Remove promotions where supported.",
  },
  {
    resource: "promotion",
    action: "analytics",
    label: "View promotion analytics",
    description: "See promotion reporting and performance.",
  },
  {
    resource: "shop",
    action: "view",
    label: "View shop",
    description: "Open shop item and order records.",
  },
  {
    resource: "shop",
    action: "list",
    label: "Browse shop",
    description: "See shop items and orders for this hiive.",
  },
  {
    resource: "shop",
    action: "create",
    label: "Create shop items",
    description: "List new items in the hiive shop.",
  },
  {
    resource: "shop",
    action: "update",
    label: "Edit shop items",
    description: "Update item setup, stock, and availability.",
  },
  {
    resource: "shop",
    action: "delete",
    label: "Delete shop items",
    description: "Remove shop items where supported.",
  },
  {
    resource: "shop",
    action: "fulfill",
    label: "Fulfill orders",
    description: "Mark orders shipped, delivered, or cancelled.",
  },
  {
    resource: "campaign",
    action: "view",
    label: "View campaigns",
    description: "Open campaign records and revisions.",
  },
  {
    resource: "campaign",
    action: "list",
    label: "Browse campaigns",
    description: "See campaigns for this hiive.",
  },
  {
    resource: "campaign",
    action: "create",
    label: "Create campaigns",
    description: "Draft campaign shells and audience snapshots.",
  },
  {
    resource: "campaign",
    action: "update",
    label: "Edit campaigns",
    description: "Update campaign setup before approval.",
  },
  {
    resource: "campaign",
    action: "send",
    label: "Test and submit campaigns",
    description: "Send tests and submit campaigns for platform review.",
  },
  {
    resource: "campaign",
    action: "analytics",
    label: "View campaign analytics",
    description: "See delivery and engagement reporting.",
  },
  {
    resource: "finance",
    action: "view",
    label: "View finance",
    description: "See payout summaries and settlement status.",
  },
  {
    resource: "finance",
    action: "view_documents",
    label: "View payout documents",
    description: "Open settlement statements and payout bills.",
  },
  {
    resource: "finance",
    action: "manage_accounts",
    label: "Manage payout accounts",
    description: "Update payout account details for this hiive.",
  },
  {
    resource: "finance",
    action: "approve",
    label: "Approve payouts",
    description: "Approve payout batches where allowed.",
  },
  {
    resource: "finance",
    action: "export",
    label: "Export payouts",
    description: "Export payout batches for manual bank operations.",
  },
  {
    resource: "finance",
    action: "mark_paid",
    label: "Mark payouts paid",
    description: "Record manual payout settlement results.",
  },
  {
    resource: "finance",
    action: "adjust",
    label: "Adjust payouts",
    description: "Create finance adjustments and corrections.",
  },
] as const satisfies ReadonlyArray<{
  resource: keyof HiivePermission;
  action: string;
  label: string;
  description: string;
}>;

export type QueenModeratorPermissionOption =
  (typeof QUEEN_MODERATOR_PERMISSION_OPTIONS)[number];

export const user = ac.newRole({
  hiive: ["view", "list"],
  venue: [],
  booking: [],
  event: ["view"],
  queen: [],
  membership: ["join", "leave", "view"],
  post: ["view", "list", "react", "comment"],
  chat: ["view", "join"],
  message: ["view", "list", "react"],
  tier: ["view", "list"],
  promotion: [],
  campaign: [],
  finance: [],
});

export const admin = ac.newRole({
  hiive: ["create", "update", "list"],
  venue: ["create", "view", "update", "delete", "list"],
  booking: ["create", "view", "update", "cancel", "refund", "list"],
  event: [
    "view",
    "create",
    "update",
    "delete",
    "manage_tickets",
    "manage_schedule",
    "check_in",
  ],
  tier: ["create", "view", "update", "delete", "list"],
  queen: ["create", "update", "view", "delete", "list"],
  membership: ["invite", "approve", "reject", "ban", "unban", "view", "list"],
  post: [
    "create",
    "update",
    "view",
    "delete",
    "list",
    "react",
    "comment",
    "pin",
  ],
  chat: ["create", "view", "update", "delete", "list", "join", "leave"],
  message: ["create", "view", "update", "delete", "list", "react"],
  promotion: ["create", "view", "update", "delete", "list", "analytics"],
  campaign: [
    "create",
    "view",
    "update",
    "delete",
    "list",
    "send",
    "approve",
    "analytics",
  ],
  finance: [
    "view",
    "manage_accounts",
    "view_documents",
    "approve",
    "export",
    "mark_paid",
    "adjust",
  ],
  credit: ["view", "issue", "revoke", "adjust"],
  referral: ["view", "configure", "approve", "void", "pause"],
  adminSection: [...ADMIN_SECTIONS],
  ...adminAc.statements,
});

// Restricted admin-panel role. Baseline grants nothing; the real grant lives in
// the per-user `user.adminPermissions` override (mirrors queen moderators).
// "overview" is always available to any panel user (granted in the guard).
export const admin_staff = ac.newRole({
  adminSection: [],
  finance: [],
  promotion: [],
  campaign: [],
  credit: [],
  referral: [],
});

export const queen_admin = ac.newRole({
  hiive: ["list"],
  venue: ["create", "view", "update", "delete", "list"],
  booking: ["create", "view", "update", "cancel", "list"],
  event: [
    "view",
    "create",
    "update",
    "delete",
    "manage_tickets",
    "manage_schedule",
    "check_in",
  ],
  tier: ["view", "list"],
  queen: ["view", "update", "list"],
  membership: ["invite", "approve", "reject", "ban", "unban", "view", "list"],
  post: [
    "view",
    "list",
    "create",
    "update",
    "delete",
    "react",
    "comment",
    "pin",
  ],
  chat: ["create", "view", "update", "delete", "list", "join", "leave"],
  message: ["create", "view", "update", "delete", "list", "react"],
  promotion: ["create", "view", "update", "delete", "list", "analytics"],
  campaign: ["create", "view", "update", "delete", "list", "send", "analytics"],
  finance: ["view", "manage_accounts", "view_documents"],
  shop: ["create", "view", "update", "delete", "list", "fulfill"],
});

export const queen_moderator = ac.newRole({
  hiive: ["list"],
  venue: ["view", "list"],
  booking: ["view", "list"],
  event: ["view", "check_in", "manage_schedule"],
  queen: [],
  membership: ["view", "list"],
  post: [
    "view",
    "list",
    "create",
    "update",
    "delete",
    "react",
    "comment",
    "pin",
  ],
  chat: ["view", "update", "list", "join", "leave"],
  message: ["create", "view", "update", "delete", "list", "react"],
  tier: ["view", "list"],
  promotion: ["view", "list"],
  campaign: ["view", "list"],
  finance: ["view", "view_documents"],
  shop: ["view", "list"],
});

export const hiive_member = ac.newRole({
  hiive: ["view"],
  venue: [],
  booking: [],
  event: ["view"],
  queen: [],
  membership: ["leave", "view"],
  post: ["view", "list", "react", "comment"],
  chat: ["view", "join", "leave"],
  message: ["create", "view", "list", "react"],
  tier: ["view", "list"],
  promotion: [],
  campaign: [],
  finance: [],
});

// ──────────────────────────────────────────────────────────────────────────────
// PLATFORM ADMIN PANEL PERMISSIONS (super_admin = "admin" role; restricted = "admin_staff")
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Per-user override stored on `user.admin_permissions` for admin_staff members.
 * `adminSection` lists which panel areas they may open; `finance` (and other
 * resources) carry fine-grained actions within a section (e.g. who may approve).
 */
export type AdminPermission = Partial<{
  adminSection: AdminSection[];
  finance: (typeof financeStatements)[number][];
  promotion: (typeof promotionStatements)[number][];
  credit: (typeof creditStatements)[number][];
  referral: (typeof referralStatements)[number][];
}>;

// Sections every panel user can always reach (not grantable/revocable).
export const ALWAYS_ALLOWED_ADMIN_SECTIONS: AdminSection[] = ["overview"];
// Sections only a super admin may reach (never granted to staff).
export const SUPER_ADMIN_ONLY_SECTIONS: AdminSection[] = ["team"];

export const ADMIN_SECTION_META: Record<
  AdminSection,
  { label: string; description: string }
> = {
  overview: { label: "Overview", description: "Admin dashboard landing." },
  hiives: { label: "Hiives", description: "Hiive setup, tiers, and queens." },
  venues: { label: "Venues", description: "Venue catalog and operations." },
  events: { label: "Events", description: "Event catalog and operations." },
  games: { label: "Games", description: "Games administration." },
  movement: {
    label: "Movement",
    description: "Territory reviews and movement ops.",
  },
  users: { label: "Users", description: "User directory and account actions." },
  finance: {
    label: "Finance",
    description: "Payouts, settlement, and reconciliation.",
  },
  promotions: {
    label: "Promotions",
    description: "Platform promotions and coupons.",
  },
  offers: {
    label: "Offers",
    description: "Wallet cards and partner offers.",
  },
  shop: {
    label: "Shop",
    description: "Products, listing review, orders, inventory, and delivery.",
  },
  credits: {
    label: "Hiive Money",
    description:
      "Issue platform credits, review balances, and track liability.",
  },
  referrals: {
    label: "Referrals",
    description:
      "Referral program settings, review queue, funnel, leaderboard, and fraud.",
  },
  notifications: {
    label: "Campaigns",
    description: "Campaigns, audience previews, approvals, and delivery.",
  },
  reports: { label: "Reports", description: "User reports and moderation." },
  reviews: { label: "Reviews", description: "Review moderation." },
  advanced: { label: "Advanced", description: "Advanced platform settings." },
  team: {
    label: "Team & roles",
    description: "Manage admin staff and their permissions.",
  },
};

/** Map a /admin pathname to the section it belongs to. */
export function pathnameToAdminSection(pathname: string): AdminSection | null {
  if (pathname === "/admin") return "overview";
  if (!pathname.startsWith("/admin/")) return null;
  const first = pathname.slice("/admin/".length).split("/")[0];
  return (ADMIN_SECTIONS as readonly string[]).includes(first)
    ? (first as AdminSection)
    : null;
}

const GRANTABLE_ADMIN_SECTIONS = ADMIN_SECTIONS.filter(
  (section) =>
    !ALWAYS_ALLOWED_ADMIN_SECTIONS.includes(section) &&
    !SUPER_ADMIN_ONLY_SECTIONS.includes(section),
);

export const ADMIN_PERMISSION_RESOURCE_META: Record<
  "adminSection" | "finance" | "credit" | "referral",
  { label: string; description: string }
> = {
  adminSection: {
    label: "Panel sections",
    description: "Which areas of the admin panel this person can open.",
  },
  finance: {
    label: "Finance actions",
    description:
      "Fine-grained payout actions. Approval still requires segregation of duties.",
  },
  credit: {
    label: "Hiive Money actions",
    description:
      "Who may issue, revoke, or adjust platform credits. Viewing is separate.",
  },
  referral: {
    label: "Referral actions",
    description:
      "Who may change the referral economics, approve held rewards, or pause the program.",
  },
};

const financeActionOptions = [
  {
    resource: "finance" as const,
    action: "view",
    label: "View finance",
    description: "Open payouts, batches, and reconciliation.",
  },
  {
    resource: "finance" as const,
    action: "view_documents",
    label: "View documents",
    description: "Open settlement statements and payout bills.",
  },
  {
    resource: "finance" as const,
    action: "approve",
    label: "Approve payouts",
    description: "Approve payout batches (cannot approve own statements).",
  },
  {
    resource: "finance" as const,
    action: "export",
    label: "Export / execute",
    description: "Export approved batches through the bank adapter.",
  },
  {
    resource: "finance" as const,
    action: "mark_paid",
    label: "Record results",
    description: "Mark payouts paid or failed.",
  },
  {
    resource: "finance" as const,
    action: "adjust",
    label: "Adjust & configure",
    description: "Edit policies, create cycles, and make adjustments.",
  },
];

const creditActionOptions = [
  {
    resource: "credit" as const,
    action: "view",
    label: "View Hiive Money",
    description: "Open balances, ledger, and the liability dashboard.",
  },
  {
    resource: "credit" as const,
    action: "issue",
    label: "Issue credits",
    description: "Grant credits to a person or a segment.",
  },
  {
    resource: "credit" as const,
    action: "revoke",
    label: "Revoke credits",
    description: "Claw back an issued grant.",
  },
  {
    resource: "credit" as const,
    action: "adjust",
    label: "Adjust balances",
    description: "Make manual balance corrections.",
  },
];

const referralActionOptions = [
  {
    resource: "referral" as const,
    action: "view",
    label: "View referrals",
    description:
      "Open the funnel, cost, advocate leaderboard, and fraud views.",
  },
  {
    resource: "referral" as const,
    action: "configure",
    label: "Configure program",
    description:
      "Change reward amounts, triggers, caps, and windows. Affects platform cost.",
  },
  {
    resource: "referral" as const,
    action: "approve",
    label: "Approve rewards",
    description: "Release held rewards from the review queue.",
  },
  {
    resource: "referral" as const,
    action: "void",
    label: "Void rewards",
    description: "Cancel unclaimed rewards or a whole referral.",
  },
  {
    resource: "referral" as const,
    action: "pause",
    label: "Pause program",
    description: "Stop new attribution and ripening platform-wide.",
  },
];

export const ADMIN_PERMISSION_OPTIONS = [
  ...GRANTABLE_ADMIN_SECTIONS.map((section) => ({
    resource: "adminSection" as const,
    action: section,
    label: ADMIN_SECTION_META[section].label,
    description: ADMIN_SECTION_META[section].description,
  })),
  ...financeActionOptions,
  ...creditActionOptions,
  ...referralActionOptions,
] as const satisfies ReadonlyArray<{
  resource: "adminSection" | "finance" | "credit" | "referral";
  action: string;
  label: string;
  description: string;
}>;

export type AdminPermissionOption = (typeof ADMIN_PERMISSION_OPTIONS)[number];
